import { useEffect, useRef, useState } from "react";

interface UseConnectionStatusOptions {
  offlineDelay?: number;
  minVisibleTime?: number;
}

export const useConnectionStatus = ({
  offlineDelay = 3000,
  minVisibleTime = 2000,
}: UseConnectionStatusOptions = {}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [shouldShowOfflineModal, setShouldShowOfflineModal] = useState(false);

  const offlineTimeoutRef = useRef<NodeJS.Timeout>();
  const modalVisibleTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
        offlineTimeoutRef.current = undefined;
      }

      // If the modal is already showing, we want to ensure it's visible for a minimum duration
      // before it's hidden. This prevents it from flashing on the screen.
      if (shouldShowOfflineModal) {
        modalVisibleTimeoutRef.current = setTimeout(() => {
          setShouldShowOfflineModal(false);
        }, minVisibleTime);
      } else {
        setShouldShowOfflineModal(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);

      // If there's a timeout to hide the modal, we should clear it because we are offline again.
      if (modalVisibleTimeoutRef.current) {
        clearTimeout(modalVisibleTimeoutRef.current);
        modalVisibleTimeoutRef.current = undefined;
      }

      offlineTimeoutRef.current = setTimeout(() => {
        setShouldShowOfflineModal(true);
      }, offlineDelay);
    };

    setIsOnline(navigator.onLine);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }
      if (modalVisibleTimeoutRef.current) {
        clearTimeout(modalVisibleTimeoutRef.current);
      }
    };
  }, [offlineDelay, minVisibleTime, shouldShowOfflineModal]);

  return {
    isOnline,
    shouldShowOfflineModal,
  };
};
